import type React from "react"

import {
  addDays,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isSameMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { Button } from "@workspace/web-ui/components/button"
import { cn } from "@workspace/web-ui/lib/utils"
import {
  addHoursToDate,
  CalendarDndProvider,
  type CalendarEvent,
  type CalendarView,
  EventDialog,
  EventGap,
  EventHeight,
  MonthView,
  WeekCellsHeight,
} from "~/features/dashboard/pages/calendar/components"
import type { AppointmentEvent, RefillEvent } from "./types"
import { DayAppointment } from "./day-appointment"
import { ModWeekView } from "./week-view-mod"


export interface EventCalendarProps {
  events?: CalendarEvent[]
  timeRange?: string  
  onEventAdd?: (event: CalendarEvent) => void
  onEventUpdate?: (event: CalendarEvent) => void
  onEventDelete?: (eventId: string) => void
  className?: string
  view: CalendarView
  setView: (view: CalendarView) => void
}

export function CareEventCalendar({
  events = [],
  onEventAdd,
  onEventUpdate,
  onEventDelete,
  className,
  view = "month",
  setView,
}: EventCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input, textarea or contentEditable element
      // or if the event dialog is open
      if (
        isEventDialogOpen ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return
      }

      switch (e.key.toLowerCase()) {
        case "m":
          setView("month")
          break
        case "w":
          setView("week")
          break
        case "d":
          setView("day")
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventDialogOpen])

  const handlePrevious = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1))
    } else if (view === "week") {
      setCurrentDate(subWeeks(currentDate, 1))
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, -1))
    }
  }

  const handleNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1))
    } else if (view === "week") {
      setCurrentDate(addWeeks(currentDate, 1))
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, 1))
    }
  }

  const handleEventSelect = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setIsEventDialogOpen(true)
  }

  const handleEventCreate = (startTime: Date) => {

    // Snap to 15-minute intervals
    const minutes = startTime.getMinutes()
    const remainder = minutes % 15
    if (remainder !== 0) {
      if (remainder < 7.5) {
        startTime.setMinutes(minutes - remainder)
      } else {
        startTime.setMinutes(minutes + (15 - remainder))
      }
      startTime.setSeconds(0)
      startTime.setMilliseconds(0)
    }

    // Check if we're in the refills view by looking at existing events
    const isRefillView = events.length > 0 && events.every(event => event.type === 'refill')
    // const isConsultationView = events.length > 0 && events.every(event => event.type === 'consultation')

    if (isRefillView) {
      const newRefill: RefillEvent = {
        id: "",
        type: "refill",
        title: "",
        description: "",
        start: startTime,
        end: addHoursToDate(startTime, 1),
        allDay: false,
        prescriptionId: "",
        clientName: "",
        medication: "",
        request_datetime: "",
        quantity: 0,
        status: "pending"
      }
      setSelectedEvent(newRefill)
    } else {
      const newAppointment: AppointmentEvent = {
        id: "",
        type: "appointment",
        title: "",
        description: "",
        start: startTime,
        end: addHoursToDate(startTime, 1),
        allDay: false,
        location: "",
        clientName: "",
        email: "",
        status: "scheduled"
      }
      setSelectedEvent(newAppointment)
    }
    setIsEventDialogOpen(true)
  }

  const handleEventSave = (event: CalendarEvent) => {
    if (event.id) {
      onEventUpdate?.(event)
      // Show toast notification when an event is updated
      toast(`Event "${event.title}" updated`, {
        description: format(new Date(event.start), "MMM d, yyyy"),
        position: "bottom-left",
      })
    } else {
      onEventAdd?.({
        ...event,
        id: Math.random().toString(36).substring(2, 11),
      })
      // Show toast notification when an event is added
      toast(`Event "${event.title}" added`, {
        description: format(new Date(event.start), "MMM d, yyyy"),
        position: "bottom-left",
      })
    }
    setIsEventDialogOpen(false)
    setSelectedEvent(null)
  }

  const handleEventDelete = (eventId: string) => {
    const deletedEvent = events.find((e) => e.id === eventId)
    onEventDelete?.(eventId)
    setIsEventDialogOpen(false)
    setSelectedEvent(null)

    // Show toast notification when an event is deleted
    if (deletedEvent) {
      toast(`Event "${deletedEvent.title}" deleted`, {
        description: format(new Date(deletedEvent.start), "MMM d, yyyy"),
        position: "bottom-left",
      })
    }
  }

  const handleEventUpdate = (updatedEvent: CalendarEvent) => {
    onEventUpdate?.(updatedEvent)

    // Show toast notification when an event is updated via drag and drop
    toast(`Event "${updatedEvent.title}" moved`, {
      description: format(new Date(updatedEvent.start), "MMM d, yyyy"),
      position: "bottom-left",
    })
  }

  const viewTitle = useMemo(() => {
    if (view === "month") {
      return format(currentDate, "MMMM yyyy")
    } else if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 })
      const end = endOfWeek(currentDate, { weekStartsOn: 0 })
      if (isSameMonth(start, end)) {
        return format(start, "MMMM yyyy")
      } else {
        return `${format(start, "MMM")} - ${format(end, "MMM yyyy")}`
      }
    } else if (view === "day") {
      return format(currentDate, "d MMMM, yyyy")
    } else {
      return format(currentDate, "MMMM yyyy")
    }
  }, [currentDate, view])

  // Simplified date format for mobile week view
  const mobileWeekTitle = useMemo(() => {
    if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 })
      const end = endOfWeek(currentDate, { weekStartsOn: 1 })
      return `${format(start, "d")} - ${format(end, "d MMM")}`
    }
    return ""
  }, [currentDate, view])

  return (
    <div
      className="flex flex-1 flex-col rounded-lg border"
      style={
        {
          "--event-height": `${EventHeight}px`,
          "--event-gap": `${EventGap}px`,
          "--week-cells-height": `${WeekCellsHeight}px`,
        } as React.CSSProperties
      }
    >
      <CalendarDndProvider onEventUpdate={handleEventUpdate}>
        <div className={cn("flex items-center bg-white rounded-t-[12px] justify-between p-2 sm:p-4", className)}>
          <div className="flex items-center justify-between gap-1 sm:gap-4">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handlePrevious} 
                aria-label="Previous"
                className="h-8 w-8 sm:h-10 sm:w-10"
              >
                <ChevronLeftIcon size={16} aria-hidden="true" />
              </Button>
              
              <h2 className="text-xs font-semibold sm:text-sm md:text-lg whitespace-nowrap px-1 sm:px-2">
                {view === "week" ? (
                  <>
                    <span className="md:hidden">{mobileWeekTitle}</span>
                    <span className="hidden md:inline">
                      {`${format(startOfWeek(currentDate, { weekStartsOn: 1 }), "d")} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM, yyyy")}`}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="md:hidden truncate">
                      {view === "day" ? format(currentDate, "d MMM") : format(currentDate, "MMM yyyy")}
                    </span>
                    <span className="hidden md:inline">{viewTitle}</span>
                  </>
                )}
              </h2>
              
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleNext} 
                aria-label="Next"
                className="h-8 w-8 sm:h-10 sm:w-10"
              >
                <ChevronRightIcon size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 bg-white rounded-b-[12px] flex-col">
          {view === "month" && (
            <MonthView
              currentDate={currentDate}
              events={events}
              onEventSelect={handleEventSelect}
              onEventCreate={handleEventCreate}
            />
          )}
          {view === "week" && (
            <ModWeekView currentDate={currentDate} events={events} onEventSelect={handleEventSelect} />
          )}
          {view === "day" && (
            <DayAppointment events={events} currentDate={currentDate} />
          )}
        </div>

        <EventDialog
          event={selectedEvent}
          isOpen={isEventDialogOpen}
          onClose={() => {
            setIsEventDialogOpen(false)
            setSelectedEvent(null)
          }}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
        />
        
        {/* Legend section - made responsive */}
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-3 sm:py-5 px-2 sm:px-4 text-center">
          {events.some(event => event.type === 'appointment') && (
            <>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-orange-400"></span>
                <span className="text-xs sm:text-sm text-gray-600">Ongoing</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-green-500"></span>
                <span className="text-xs sm:text-sm text-gray-600">Completed</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm border border-green-600"></span>
                <span className="text-xs sm:text-sm text-gray-600">Upcoming</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-gray-300"></span>
                <span className="text-xs sm:text-sm text-gray-600">Cancelled</span>
              </div>
            </>
          )}
          {events.some(event => event.type === 'refill') && (
            <>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-orange-400"></span>
                <span className="text-xs sm:text-sm text-gray-600">Pending</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm border bg-blue-600"></span>
                <span className="text-xs sm:text-sm text-gray-600">Accepted</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-red-600"></span>
                <span className="text-xs sm:text-sm text-gray-600">Rejected</span>
              </div>
            </>
          )}
        </div>
      </CalendarDndProvider>
    </div>
  )
}